import { useState, useEffect } from 'react';
import { isDemoBrand } from '../utils/constants';
import { formatDateForAPI } from '../utils/formatters';
import { fetchWithBrand } from '../utils/api';
import {
  Eye,
  ShoppingCart,
  MousePointerClick,
  CreditCard,
  Package,
} from 'lucide-react';

/**
 * Process raw GA4 events data into a 5-step funnel structure.
 */
function processFunnelData(eventsData) {
  const funnelSteps = [
    { eventName: 'view_item', displayName: 'Product Views', icon: Eye },
    { eventName: 'add_to_cart', displayName: 'Add to Cart', icon: ShoppingCart },
    { eventName: 'view_cart', displayName: 'View Cart', icon: MousePointerClick },
    { eventName: 'begin_checkout', displayName: 'Checkout', icon: CreditCard },
    { eventName: 'purchase', displayName: 'Purchase', icon: Package },
  ];

  const eventCounts = {};
  if (eventsData && eventsData.summaryByEvent) {
    eventsData.summaryByEvent.forEach(event => {
      eventCounts[event.eventName] = Number(event.totalEventCount || 0);
    });
  }

  let baseCount = Math.max(...funnelSteps.map(step => eventCounts[step.eventName] || 0));

  const funnelStepsWithData = [];
  funnelSteps.forEach((step, index) => {
    const count = eventCounts[step.eventName] || 0;
    let dropoff = 0;
    if (index > 0) {
      const prevCount = funnelStepsWithData[index - 1].value;
      if (prevCount > 0) {
        dropoff = ((prevCount - count) / prevCount * 100);
      }
    }
    funnelStepsWithData.push({
      name: step.displayName,
      value: count,
      dropoff: Math.round(dropoff * 10) / 10,
      icon: step.icon,
    });
  });

  const purchaseCount = eventCounts['purchase'] || 0;
  const totalRevenue = purchaseCount * 150;
  const conversionRate = baseCount > 0 ? (purchaseCount / baseCount * 100) : 0;
  const dropoffRate = 100 - conversionRate;

  const summary = {
    conversionRate: Math.round(conversionRate * 10) / 10,
    avgTimeToConvert: '1.8',
    dropoffRate: Math.round(dropoffRate * 10) / 10,
    revenue: totalRevenue,
    avgOrderValue: 150,
  };

  return { steps: funnelStepsWithData, summary };
}

/**
 * Fetches ga-events-raw, processes into 5-step funnel.
 */
export function useFunnel(brandId, dateRange, startDate, endDate) {
  const [funnelData, setFunnelData] = useState({
    steps: [
      { name: 'Product Views', value: 0, percentage: 0, icon: Eye, vsLastPeriod: 0, dropoff: 0 },
      { name: 'Add to Cart', value: 0, percentage: 0, dropoff: 0, icon: ShoppingCart, vsLastPeriod: 0 },
      { name: 'View Cart', value: 0, percentage: 0, dropoff: 0, icon: MousePointerClick, vsLastPeriod: 0 },
      { name: 'Checkout', value: 0, percentage: 0, dropoff: 0, icon: CreditCard, vsLastPeriod: 0 },
      { name: 'Purchase', value: 0, percentage: 0, dropoff: 0, icon: Package, vsLastPeriod: 0 },
    ],
    summary: {
      conversionRate: 0,
      avgTimeToConvert: '0',
      dropoffRate: 0,
      revenue: 0,
      avgOrderValue: 0,
    },
  });
  const [eventsData, setEventsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (dateRange === 'custom' && (!startDate || !endDate)) return;
    if (!brandId) return;

    const fetchEventsData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Demo funnel data
        if (isDemoBrand(brandId)) {
          const demoFunnel = {
            steps: [
              { name: 'Product Views', value: 45200, percentage: 100, dropoff: 0, icon: Eye, vsLastPeriod: 12.5 },
              { name: 'Add to Cart', value: 8150, percentage: 18.0, dropoff: 82.0, icon: ShoppingCart, vsLastPeriod: 8.3 },
              { name: 'View Cart', value: 5420, percentage: 12.0, dropoff: 33.5, icon: MousePointerClick, vsLastPeriod: 15.2 },
              { name: 'Checkout', value: 2980, percentage: 6.6, dropoff: 45.0, icon: CreditCard, vsLastPeriod: 6.8 },
              { name: 'Purchase', value: 1850, percentage: 4.1, dropoff: 37.9, icon: Package, vsLastPeriod: 11.2 },
            ],
            summary: {
              conversionRate: 4.1,
              avgTimeToConvert: '2.3',
              dropoffRate: 95.9,
              revenue: 1620000,
              avgOrderValue: 876,
            },
          };
          setFunnelData(demoFunnel);
          setEventsData([]);
          setIsLoading(false);
          return;
        }

        let apiUrl = `/api/google-ads/ga-events-raw?dateRange=${dateRange}`;
        if (dateRange === 'custom' && startDate && endDate) {
          const startStr = formatDateForAPI(startDate);
          const endStr = formatDateForAPI(endDate);
          apiUrl = `/api/google-ads/ga-events-raw?dateRange=custom&startDate=${startStr}&endDate=${endStr}`;
        }

        const response = await fetchWithBrand(apiUrl, brandId);
        if (!response.ok) {
          throw new Error(`API Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const processedFunnel = processFunnelData(data);
        setFunnelData(processedFunnel);
        setEventsData(data);
      } catch (err) {
        setError(err.message || 'Failed to fetch events data');
        setEventsData(null);
        const processedFunnel = processFunnelData(null);
        setFunnelData(processedFunnel);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventsData();
  }, [brandId, dateRange, startDate, endDate]);

  return {
    funnelData,
    eventsData,
    isLoading,
    error,
  };
}
